import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  Armchair,
  BedDouble,
  CakeSlice,
  Camera,
  ChefHat,
  Flower2,
  Loader2,
  MapPin,
  MessageCircle,
  Music,
  Truck,
  Utensils,
} from 'lucide-react';
import { useReturningLead } from '@/components/wizard/useReturningLead';
import { useWizardRoute } from '@/components/wizard/useWizardRoute';
import { ensureQuoteFlowAssignment } from '@/lib/quoteFlowAssignment';
import { cn } from '@/lib/utils';
import Footer from '@/components/layout/Footer';
import { getWhatsAppUrl } from '@/lib/constants/contactConstants';
import { useThemeColor } from '@/hooks/useThemeColor';
import { PRIMARY_LOGO_URL } from '@/lib/constants/heroImage';
import { HeroBackdrop } from '@/components/layout/HeroBackdrop';
import { LandingPriceAnchor } from '@/components/layout/LandingPriceAnchor';
import { closeQuoteSession, track } from '@/lib/tracking';
import { hasResumableProgress } from '@/components/wizard/wizardProgress';
import { whenIdleAfterLoad } from '@/lib/idle';

// The wizard (phone input, flags, framer-motion...) is a big chunk that only matters once someone taps the CTA, so it
// is kept out of the entry bundle. It is fetched when the browser is idle after the page has loaded, so the CTA still
// opens instantly without competing with the hero/LCP for bandwidth.
const QuoteWizardDialog = lazy(() =>
  import('@/components/wizard/QuoteWizardDialog').then((m) => ({ default: m.QuoteWizardDialog })),
);
const WIZARD_PRELOAD_TIMEOUT_MS = 4000;

const INCLUDED_SERVICES = [
  { label: 'Locación', icon: MapPin },
  { label: 'Banquete', icon: ChefHat },
  { label: 'Menaje', icon: Utensils },
  { label: 'Mobiliario', icon: Armchair },
  { label: 'Decoración', icon: Flower2 },
  { label: 'Música', icon: Music },
  { label: 'Fotografía', icon: Camera },
  { label: 'Postres', icon: CakeSlice },
  { label: 'Logística', icon: Truck },
  { label: 'Hospedaje', icon: BedDouble },
];

// Top of the hero photo with its scrim applied, so the browser toolbar blends into the image
const HERO_THEME_COLOR = '#242a25';

const Index = () => {
  useThemeColor(HERO_THEME_COLOR);
  const navigate = useNavigate();
  const { open: wizardOpen, setOpen: setWizardOpen } = useWizardRoute();
  // Decide this visitor's quote arm now, so the form is ready with its wording when the wizard opens
  useEffect(() => {
    void ensureQuoteFlowAssignment();
  }, []);
  // Someone who already submitted lands on their next steps, not on an empty wizard
  const { state: returning, clear: clearReturning } = useReturningLead();
  const checkingReturning = returning.status === 'loading';
  const converted = returning.status === 'converted';

  // Mount the lazy wizard once the page is idle (or immediately when the URL opens it)
  const [wizardPreloaded, setWizardPreloaded] = useState(false);
  useEffect(() => whenIdleAfterLoad(() => setWizardPreloaded(true), WIZARD_PRELOAD_TIMEOUT_MS), []);

  const landingTrackedRef = useRef(false);
  useEffect(() => {
    if (landingTrackedRef.current) return;
    landingTrackedRef.current = true;
    track('Landing Viewed', { has_wizard_open: wizardOpen });
    // Once per page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Covers every way the wizard closes: X, Esc, browser Back, and after a submit
  useEffect(() => {
    if (!wizardOpen) closeQuoteSession('closed');
  }, [wizardOpen]);

  // Re-read on every render while the wizard is closed (closing it can create or clear a draft). While it is open the
  // label holds, so the CTA behind the card doesn't flip to "Cotizar mi boda" as the card comes in.
  const hasDraftRef = useRef(false);
  if (!wizardOpen) hasDraftRef.current = hasResumableProgress('lead');
  const hasDraft = hasDraftRef.current;
  const quoteCtaLabel = hasDraft ? 'Continuar mi cotización' : 'Cotizar mi boda';
  // A submitted quote beats a draft (submitting clears the draft); an account beats both
  const ctaLabel = converted
    ? 'Entrar a mi evento'
    : returning.status === 'returning'
      ? 'Ver mi cotización'
      : quoteCtaLabel;

  return (
    // The negative margins undo the body's safe-area padding so the photo stays full-bleed under the notch in landscape;
    // the content below adds the insets back
    <div className="min-h-screen bg-background overflow-x-hidden ml-[calc(-1*env(safe-area-inset-left))] mr-[calc(-1*env(safe-area-inset-right))]">
      {/* Hero: full-bleed venue photo with the content on a frosted panel */}
      <section className="relative min-h-[100svh] flex flex-col overflow-hidden w-full">
        {/* The sway waits for the wizard preload so they don't share an idle slot, and holds still under the wizard */}
        <HeroBackdrop motionCanStart={wizardPreloaded} motionPaused={wizardOpen} />

        <div className="relative z-10 flex-1 flex items-end [@media(max-height:700px)_and_(orientation:landscape)]:!items-center justify-center md:justify-start pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] md:pl-[max(4rem,env(safe-area-inset-left))] md:pr-[max(4rem,env(safe-area-inset-right))] pt-16 pb-[calc(2.5rem+env(safe-area-inset-bottom))] md:pt-12 md:pb-[10svh] [@media(max-height:700px)_and_(orientation:landscape)]:pt-4 [@media(max-height:700px)_and_(orientation:landscape)]:pb-[calc(1rem+env(safe-area-inset-bottom))]">
          {/* Desktop: 6 of 12 columns at 1440px (64px margins, 24px gutters) */}
          <div className="w-full max-w-3xl md:max-w-[38rem] lg:max-w-[644px] text-center md:text-left">
            {/* Phones and tablets: pinned top-center; on a phone it stays visible above the wizard's sheet (steps 1–2).
                Desktop: in the content column right above the headline (the side sheet covers the right side), half
                the column wide (310px) with 40px to the headline. The logo file is trimmed, so these are the mark's
                own size and position. */}
            <img
              src={PRIMARY_LOGO_URL}
              alt="Casa Aragón"
              className="absolute top-[35px] md:top-14 left-1/2 -translate-x-1/2 h-[74px] md:h-[92px] w-auto object-contain brightness-0 invert drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] lg:static lg:block lg:translate-x-0 lg:h-[132px] lg:mb-10 [@media(max-height:700px)_and_(orientation:landscape)]:!static [@media(max-height:700px)_and_(orientation:landscape)]:!block [@media(max-height:700px)_and_(orientation:landscape)]:!translate-x-0 [@media(max-height:700px)_and_(orientation:landscape)]:!h-14 [@media(max-height:700px)_and_(orientation:landscape)]:!mb-3 max-md:[@media(max-height:700px)_and_(orientation:landscape)]:!mx-auto"
            />

            {/* Phones and tablets: the copy fades out under the wizard's sheet (it doesn't move) and comes back after it
                closes; the logo and photo stay. Desktop keeps it in place, dimmed by the overlay. */}
            <div
              data-testid="landing-copy"
              inert={wizardOpen}
              className={cn(
                'transition-opacity ease-linear',
                wizardOpen ? 'duration-150 max-lg:opacity-0' : 'delay-150 duration-200'
              )}
            >
              <h1 className="text-[clamp(1.75rem,1.1rem+2.6vw,2.75rem)] leading-none tracking-tight text-white font-[250] md:font-thin mb-5 md:mb-8 text-pretty mx-auto md:mx-0 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
                Transformamos <span className="font-light text-[#E1C983]">sueños</span> en historias
                mágicas que duran <span className="font-light text-[#E1C983]">para siempre</span>
              </h1>

              {/* Services first, then the CTA last so it ends up at the bottom of the hero */}
              <div className="max-w-3xl mx-auto md:mx-0">
                {/* Dropped on short screens so the CTA below stays above the fold */}
                <p className="mb-3 text-sm md:text-base text-white/90 drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)] [@media(max-height:720px)]:hidden">
                  <span className="font-medium text-white">Un solo precio, todo incluido.</span> Sin
                  wedding planner. Sin proveedores.
                </p>
                <ul className="flex flex-wrap gap-x-4 gap-y-2 justify-center md:justify-start max-w-[30rem] mx-auto md:mx-0">
                  {INCLUDED_SERVICES.map(({ label, icon: Icon }) => (
                    <li
                      key={label}
                      className="inline-flex items-center gap-1.5 text-xs md:text-sm text-white/90 drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)]"
                    >
                      <Icon className="h-4 w-4 text-white" aria-hidden="true" />
                      {label}
                    </li>
                  ))}
                </ul>
                <LandingPriceAnchor />
              </div>

              <div className="mt-6 md:mt-8 flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full justify-center md:justify-start">
                {/* Returning visitors pick up where they left off; "Empezar de nuevo" lives inside the wizard */}
                <Button
                  size="lg"
                  className="text-lg px-10 py-6 w-full max-w-xs sm:w-auto sm:max-w-none"
                  disabled={checkingReturning}
                  aria-busy={checkingReturning}
                  onClick={() => {
                    track('Quote CTA Clicked', { source: 'landing', cta_label: ctaLabel });
                    if (converted) navigate('/auth?from=landing');
                    else setWizardOpen(true);
                  }}
                >
                  {/* Neutral while the (one small) return check runs, so the label never flashes and swaps */}
                  {checkingReturning ? (
                    <Loader2 className="h-5 w-5 animate-spin" aria-label="Cargando" />
                  ) : (
                    <>
                      {ctaLabel}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
                {/* Existing clients: a clear verb, but visually secondary to the quote CTA */}
                {!converted && (
                  <Button
                    variant="ghost"
                    size="lg"
                    className="text-base text-white/90 hover:bg-white/10 hover:text-white hover:underline underline-offset-4"
                    onClick={() => {
                      track('Client Login Clicked', { location: 'landing_hero' });
                      navigate('/auth?from=landing');
                    }}
                  >
                    Acceder a mi evento
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Held back until the return check settles: the wizard picks its starting screen once, on mount */}
          {(wizardOpen || wizardPreloaded) && (
            <Suspense fallback={null}>
              <QuoteWizardDialog
                open={wizardOpen && !checkingReturning}
                onOpenChange={setWizardOpen}
                source="landing"
                returning={returning.status === 'returning' ? returning.lead : undefined}
                onReturningCleared={clearReturning}
              />
            </Suspense>
          )}
        </div>
      </section>

      {/* Floating help: out of the conversion path, always reachable. Hidden while the wizard is open */}
      {!wizardOpen && (
        <a
          href={getWhatsAppUrl('Hola, tengo dudas sobre Casa Aragón')}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="¿Tienes dudas? Escríbenos por WhatsApp"
          onClick={() =>
            track('Contact Clicked', {
              channel: 'whatsapp',
              location: 'landing_floating_help',
              wizard_open: false,
            })
          }
          className="fixed bottom-4 right-[max(1rem,env(safe-area-inset-right))] md:bottom-6 md:right-[max(1.5rem,env(safe-area-inset-right))] z-40 inline-flex h-12 w-12 md:w-auto md:px-5 items-center justify-center gap-2 rounded-full bg-background text-brand-dark font-medium shadow-lg hover:bg-background/90 transition-colors"
        >
          <MessageCircle className="h-5 w-5" aria-hidden="true" />
          <span className="hidden md:inline">¿Tienes dudas?</span>
        </a>
      )}

      {/* Footer on the leaf pattern, below the hero */}
      <div className="relative">
        <div className="absolute inset-0 bg-leaf-pattern" />
        <div className="absolute inset-0 bg-background/70" />
        <Footer />
      </div>
    </div>
  );
};

export default Index;
